import { localized, msg, str } from '@lit/localize';
import { css, html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

import '@spectrum-web-components/button/sp-button.js';
import '@spectrum-web-components/field-label/sp-field-label.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-add.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-delete.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-close.js';
import '@spectrum-web-components/menu/sp-menu-item.js';
import '@spectrum-web-components/picker/sp-picker.js';
import '@spectrum-web-components/progress-circle/sp-progress-circle.js';
import '@spectrum-web-components/table/sp-table.js';
import '@spectrum-web-components/table/sp-table-body.js';
import '@spectrum-web-components/table/sp-table-cell.js';
import '@spectrum-web-components/table/sp-table-head.js';
import '@spectrum-web-components/table/sp-table-head-cell.js';
import '@spectrum-web-components/table/sp-table-row.js';
import '@spectrum-web-components/textfield/sp-textfield.js';

import '../../components/confirmation-dialog';
import type { InstalledPackage } from '../../api/installed-packages.js';
import { installedPackageService } from '../../services/installed-package-service.js';
import { settingsSharedStyles } from './settings-shared.styles';

@customElement('kv-settings-packages')
@localized()
export class KvSettingsPackages extends LitElement {
  static styles = [
    settingsSharedStyles,
    css`
      sp-table {
      width: 100%;
    }

      .source-pill {
        align-items: center;
        background: var(--settings-accent-bg);
        border-radius: 999px;
        color: var(--settings-accent);
        display: inline-flex;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        padding: 4px 10px;
        text-transform: uppercase;
      }

      .table-heading {
        align-items: center;
        display: flex;
        justify-content: space-between;
        padding: 14px 16px 0;
      }

      .table-heading h3 {
        margin: 0;
      }
    `,
  ];

  @state()
  accessor installedPackages: InstalledPackage[] = [];

  @state()
  accessor packagesLoading = true;

  @state()
  accessor packagesError: string | null = null;

  @state()
  accessor packageFormError: string | null = null;

  @state()
  accessor packageSaving = false;

  @state()
  accessor removingPackageIds: number[] = [];

  @state()
  accessor pendingPackageRemove: InstalledPackage | null = null;

  @state()
  accessor newPackageSource = 'n8n';

  @state()
  accessor newPackageName = '';

  @state()
  accessor newPackageNode = '';

  @state()
  accessor newPackageTask = '';

  @state()
  accessor newPackageCategory = '';

  connectedCallback() {
    super.connectedCallback();
    this.loadInstalledPackages();
  }

  private async loadInstalledPackages() {
    this.packagesLoading = true;
    this.packagesError = null;
    try {
      this.installedPackages = await installedPackageService.list();
    } catch (err) {
      this.packagesError = msg('Failed to load packages');
      console.error('Error loading packages:', err);
    } finally {
      this.packagesLoading = false;
    }
  }

  private onPackageInput(e: Event) {
    const target = e.target as HTMLInputElement;
    const value = target.value;
    switch (target.name) {
      case 'packageName':
        this.newPackageName = value;
        break;
      case 'nodeName':
        this.newPackageNode = value;
        break;
      case 'taskName':
        this.newPackageTask = value;
        break;
      case 'category':
        this.newPackageCategory = value;
        break;
      default:
        break;
    }
  }

  private onPackageSourceChange(e: Event) {
    const picker = e.target as { value?: string };
    this.newPackageSource = picker.value ?? 'n8n';
  }

  private async verifyPackageExists(packageName: string): Promise<boolean> {
    const encoded = encodeURIComponent(packageName);
    try {
      const res = await fetch(`https://registry.npmjs.org/${encoded}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (res.status === 404) return false;
      return res.ok;
    } catch (err) {
      console.error('Error checking npm registry:', err);
      return false;
    }
  }

  private async addPackage() {
    const packageName = this.newPackageName.trim();
    const nodeName = this.newPackageNode.trim();
    const taskName = this.newPackageTask.trim();
    const category = this.newPackageCategory.trim();

    if (!packageName) {
      this.packageFormError = msg('Package name is required');
      return;
    }
    if (!nodeName) {
      this.packageFormError = msg('Node name is required');
      return;
    }

    this.packageFormError = null;
    this.packageSaving = true;
    try {
      const exists = await this.verifyPackageExists(packageName);
      if (!exists) {
        this.packageFormError = msg('Package not found in npm registry');
        return;
      }

      await installedPackageService.create({
        source: this.newPackageSource,
        packageName,
        nodeName,
        taskName: taskName.length ? taskName : null,
        category: category.length ? category : null,
      });
      this.newPackageName = '';
      this.newPackageNode = '';
      this.newPackageTask = '';
      this.newPackageCategory = '';
      await this.loadInstalledPackages();
    } catch (err) {
      this.packageFormError = msg('Failed to add package');
      console.error('Error adding package:', err);
    } finally {
      this.packageSaving = false;
    }
  }

  private isRemovable(pkg: InstalledPackage): boolean {
    return pkg.source === 'n8n' && typeof pkg.id === 'number';
  }

  private isRemoving(pkg: InstalledPackage): boolean {
    return (
      typeof pkg.id === 'number' && this.removingPackageIds.includes(pkg.id)
    );
  }

  private requestRemovePackage(pkg: InstalledPackage) {
    if (!this.isRemovable(pkg) || pkg.id == null) return;
    this.pendingPackageRemove = pkg;
  }

  private cancelRemovePackage = () => {
    this.pendingPackageRemove = null;
  };

  private confirmRemovePackage = async () => {
    const pkg = this.pendingPackageRemove;
    if (!pkg || !this.isRemovable(pkg) || pkg.id == null) return;
    this.pendingPackageRemove = null;
    this.removingPackageIds = [...this.removingPackageIds, pkg.id];
    try {
      await installedPackageService.remove(pkg.id);
      await this.loadInstalledPackages();
    } catch (err) {
      this.packagesError = msg('Failed to remove package');
      console.error('Error removing package:', err);
    } finally {
      this.removingPackageIds = this.removingPackageIds.filter(
        (id) => id !== pkg.id
      );
    }
  };

  private formatPackageValue(value: string | null): string {
    return value?.length ? value : '-';
  }

  render() {
    return html`
      <section class="settings-section">
        <header class="settings-header">
          <div class="settings-title-row">
            <span class="settings-title-icon">
              <sp-icon-add size="s"></sp-icon-add>
            </span>
            <h2 class="settings-title">${msg('Packages')}</h2>
          </div>
          <p class="settings-description">
            ${msg(
              'Manage installed task packages. n8n packages can be added or removed, while built-in tasks are listed as Klarvos.'
            )}
          </p>
        </header>

        <div class="settings-panel">
          <div class="settings-panel-header">
            <div>
              <h3 class="settings-panel-title">${msg('Add package')}</h3>
              <p class="settings-panel-description">
                ${msg('Register a package and map it to the task palette.')}
              </p>
            </div>
          </div>

          <div class="settings-form-grid">
            <div class="settings-form-row">
              <sp-field-label for="package-source">${msg('Source')}</sp-field-label>
              <sp-picker
                id="package-source"
                .value=${this.newPackageSource}
                @change=${this.onPackageSourceChange}
                @click=${(e: Event) => {
                  e.preventDefault();
                  const picker = e.target as EventTarget & {
                    open?: boolean;
                  };
                  if (picker && typeof picker.open === 'boolean') {
                    picker.open = true;
                  }
                }}
              >
                <sp-menu-item value="n8n">n8n</sp-menu-item>
              </sp-picker>
            </div>

            <div class="settings-form-row">
              <sp-field-label for="package-name">${msg('Package name')}</sp-field-label>
              <sp-textfield
                id="package-name"
                name="packageName"
                placeholder="@scope/n8n-nodes-example"
                .value=${this.newPackageName}
                @input=${this.onPackageInput}
              ></sp-textfield>
            </div>

            <div class="settings-form-row">
              <sp-field-label for="package-node">${msg('Node name')}</sp-field-label>
              <sp-textfield
                id="package-node"
                name="nodeName"
                placeholder="Example"
                .value=${this.newPackageNode}
                @input=${this.onPackageInput}
              ></sp-textfield>
            </div>

            <div class="settings-form-row">
              <sp-field-label for="package-task">${msg('Task name (optional)')}</sp-field-label>
              <sp-textfield
                id="package-task"
                name="taskName"
                placeholder="example-node"
                .value=${this.newPackageTask}
                @input=${this.onPackageInput}
              ></sp-textfield>
            </div>

            <div class="settings-form-row">
              <sp-field-label for="package-category">${msg('Category (optional)')}</sp-field-label>
              <sp-textfield
                id="package-category"
                name="category"
                placeholder="n8n/example"
                .value=${this.newPackageCategory}
                @input=${this.onPackageInput}
              ></sp-textfield>
            </div>
          </div>

          <div class="settings-actions">
            <sp-button
              variant="accent"
              ?disabled=${this.packageSaving}
              @click=${this.addPackage}
            >
              <sp-icon-add slot="icon"></sp-icon-add>
              ${msg('Add package')}
            </sp-button>
          </div>
        </div>

        ${
          this.packageFormError
            ? html`
              <div class="settings-message error">
                <sp-icon-close size="s"></sp-icon-close>
                ${this.packageFormError}
              </div>
            `
            : ''
        }

        ${
          this.packagesLoading
            ? html`
              <div class="settings-loading">
                <sp-progress-circle
                  label=${msg('Loading packages')}
                  indeterminate
                ></sp-progress-circle>
                ${msg('Loading packages...')}
              </div>
            `
            : this.packagesError
              ? html`
                <div class="settings-message error">
                  <sp-icon-close size="s"></sp-icon-close>
                  ${this.packagesError}
                </div>
              `
              : this.installedPackages.length
                ? html`
                  <div class="settings-table-shell">
                    <div class="table-heading">
                      <h3 class="settings-panel-title">
                        ${msg(str`Installed packages (${this.installedPackages.length})`)}
                      </h3>
                    </div>
                    <sp-table>
                      <sp-table-head>
                        <sp-table-row>
                          <sp-table-head-cell>${msg('Source')}</sp-table-head-cell>
                          <sp-table-head-cell>${msg('Task')}</sp-table-head-cell>
                          <sp-table-head-cell>${msg('Package')}</sp-table-head-cell>
                          <sp-table-head-cell>${msg('Node')}</sp-table-head-cell>
                          <sp-table-head-cell>${msg('Actions')}</sp-table-head-cell>
                        </sp-table-row>
                      </sp-table-head>
                      <sp-table-body>
                        ${repeat(
                          this.installedPackages,
                          (pkg) => pkg.id ?? `${pkg.source}-${pkg.taskName}`,
                          (pkg) => html`
                            <sp-table-row>
                              <sp-table-cell>
                                <span class="source-pill">${pkg.source}</span>
                              </sp-table-cell>
                              <sp-table-cell>${this.formatPackageValue(pkg.taskName)}</sp-table-cell>
                              <sp-table-cell>${this.formatPackageValue(pkg.packageName)}</sp-table-cell>
                              <sp-table-cell>${this.formatPackageValue(pkg.nodeName)}</sp-table-cell>
                              <sp-table-cell>
                                ${
                                  this.isRemovable(pkg)
                                    ? html`
                                      <sp-button
                                        size="s"
                                        variant="secondary"
                                        class="settings-danger-button"
                                        ?disabled=${this.isRemoving(pkg)}
                                        @click=${() => this.requestRemovePackage(pkg)}
                                      >
                                        <sp-icon-delete slot="icon"></sp-icon-delete>
                                        ${msg('Remove')}
                                      </sp-button>
                                    `
                                    : html`<span class="muted">-</span>`
                                }
                              </sp-table-cell>
                            </sp-table-row>
                          `
                        )}
                      </sp-table-body>
                    </sp-table>
                  </div>
                `
                : html`
                  <div class="settings-empty">
                    <h3>${msg('No packages installed')}</h3>
                    <p>${msg('Add your first n8n package to populate the registry.')}</p>
                  </div>
                `
        }
        ${
          this.pendingPackageRemove
            ? html`
              <kv-confirmation-dialog
                heading=${msg('Remove package')}
                confirm-label=${msg('Remove')}
                @cancel=${this.cancelRemovePackage}
                @confirm=${this.confirmRemovePackage}
              >
                <p>
                  ${msg(
                    str`Remove package "${this.pendingPackageRemove.packageName ?? this.pendingPackageRemove.taskName}"? Tasks from this package will no longer be available for new workflow nodes.`
                  )}
                </p>
              </kv-confirmation-dialog>
            `
            : ''
        }
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kv-settings-packages': KvSettingsPackages;
  }
}
